import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS, fetchWithCredentials } from '../config/api';
import './PurchasePage.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaypal, faBitcoin, faWeixin, faAlipay } from '@fortawesome/free-brands-svg-icons';

const packages = [
  {
    id: 1,
    name: '1個月套餐',
    duration: 1,
    price: 8.9,
    currency: 'USD',
    features: ['全球電視直播', '追劇點播', '支持安卓設備']
  },
  {
    id: 2,
    name: '3個月套餐',
    duration: 3,
    price: 26.9,
    currency: 'USD',
    features: ['全球電視直播', '追劇點播', '支持安卓設備', '優先客服支持']
  },
  {
    id: 3,
    name: '半年套餐',
    duration: 6,
    price: 49.9,
    currency: 'USD',
    features: ['全球電視直播', '追劇點播', '支持安卓設備', '優先客服支持', '高清畫質']
  },
  {
    id: 4,
    name: '1年套餐',
    duration: 12,
    price: 99,
    currency: 'USD',
    features: ['全球電視直播', '追劇點播', '支持安卓設備', '優先客服支持', '高清畫質', '多設備支持']
  }
];

function PurchasePage() {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('paypal');

  const paymentMethods = [
    { id: 'paypal', name: 'PayPal', icon: <FontAwesomeIcon icon={faPaypal} />, available: true },
    { id: 'usdt', name: 'USDT', icon: <FontAwesomeIcon icon={faBitcoin} />, available: true },
    { id: 'wechat', name: '微信支付', icon: <FontAwesomeIcon icon={faWeixin} />, available: false },
    { id: 'alipay', name: '支付宝', icon: <FontAwesomeIcon icon={faAlipay} />, available: false }
  ];

  const handlePackageSelect = (pkg) => {
    if (!isLoggedIn) {
      navigate('/auth', { state: { from: { pathname: '/purchase' } } });
      return;
    }
    setSelectedPackage(pkg);
  };

  const handlePaymentMethodSelect = (methodId) => {
    const method = paymentMethods.find(m => m.id === methodId);
    if (!method.available) {
      return; // 直接返回，不执行任何操作
    }
    setPaymentMethod(methodId);
  };

  const handlePurchase = async () => {
    if (!selectedPackage) return;

    // 检查支付方式是否可用
    const method = paymentMethods.find(m => m.id === paymentMethod);
    if (!method.available) {
      return; // 直接返回，不执行任何操作
    }

    setLoading(true);
    setError('');

    try {
      console.log('发送订单数据:', {
        packageId: selectedPackage.id,
        amount: selectedPackage.price,
        currency: selectedPackage.currency,
        paymentMethod
      });

      const response = await fetchWithCredentials(API_ENDPOINTS.ORDERS.CREATE, {
        method: 'POST',
        body: JSON.stringify({
          packageId: selectedPackage.id,
          amount: selectedPackage.price,
          currency: selectedPackage.currency,
          paymentMethod
        })
      });

      if (response && response.orderId) {
        // 根据不同的支付方式处理
        if (paymentMethod === 'paypal') {
          window.open(response.paypalUrl, '_blank');
        } else {
          // 其他支付方式跳转到支付页面
          navigate(`/payment/${response.orderId}`);
        }
      } else {
        setError('創建訂單失敗，請稍後重試');
      }
    } catch (err) {
      console.error('創建訂單錯誤:', err);
      setError(err.message || '創建訂單失敗，請稍後重試');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="purchase-page">
      <div className="container">
        <div className="packages-grid">
          {packages.map((pkg) => (
            <div 
              key={pkg.id} 
              className={`package-card ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}
              onClick={() => handlePackageSelect(pkg)}
            >
              <h2>{pkg.name}</h2>
              <div className="price">
                <span className="amount">${pkg.price}</span>
                <span className="duration">/{pkg.duration}個月</span>
              </div>
              <ul className="features">
                {pkg.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
              <button 
                className="select-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePackageSelect(pkg);
                }}
              >
                選擇套餐
              </button>
            </div>
          ))}
        </div>

        {selectedPackage && (
          <div className="purchase-summary">
            <h2>訂單摘要</h2>
            <div className="summary-details">
              <p>套餐：{selectedPackage.name}</p>
              <p>價格：${selectedPackage.price} {selectedPackage.currency}</p>
            </div>
            <div className="payment-section">
              <div className="payment-methods">
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    className={`payment-method ${paymentMethod === method.id ? 'selected' : ''} ${!method.available ? 'unavailable' : ''}`}
                    onClick={() => handlePaymentMethodSelect(method.id)}
                    data-method={method.id}
                  >
                    <span className="method-icon">{method.icon}</span>
                    <span className="method-name">{method.name}</span>
                    {!method.available && <span className="unavailable-badge">暫未開通</span>}
                  </div>
                ))}
              </div>
              
              <button 
                className="purchase-button"
                onClick={handlePurchase}
                disabled={loading}
              >
                {loading ? '處理中...' : '立即購買'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PurchasePage; 